import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useUsers } from "@/contexts/users-provider";
import {
  getTeamUserRoleLabel,
  TeamUserRolesEnum,
} from "@/enums/team-user-role";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamUserDTO, TeamUserRole } from "@shaarp/common";
import { useState } from "react";
import { useToastError } from "../ui/use-toast";

export default function DataTableCellUserRole({ user }: { user: TeamUserDTO }) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const { isAdmin, isManager } = useTeams();
  const { isAdminMode } = useAdminMode();
  const { apiFetchUsers } = useUsers();

  const [userRole, setUserRole] = useState<TeamUserRole>(user.role);
  const [pendingRole, setPendingRole] = useState<TeamUserRole | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false);

  // Admin and Manager can change roles
  const hasPermission = isAdmin || isManager;
  const canAssignAdminRoles = isAdmin && isAdminMode;

  const handleOnChange = (selectedRole: string) => {
    setPendingRole(selectedRole as TeamUserRole);
    setConfirmDialog(true);
  };

  const doCancel = () => {
    setConfirmDialog(false);
    setPendingRole(null);
  };

  const doConfirm = async () => {
    setConfirmDialog(false);

    if (!pendingRole) {
      return;
    }

    const oldRole = userRole;
    setUserRole(pendingRole);

    try {
      await api.post(`/my/team/user/${user.id}`, {
        user: {
          role: pendingRole,
        },
      });
      apiFetchUsers({ force: true });
    } catch (e) {
      setUserRole(oldRole);
      toastError();
    } finally {
      setPendingRole(null);
    }
  };

  const isBillingRoleChange = pendingRole === TeamUserRolesEnum.BILLING;

  return (
    <>
      <Select
        defaultValue="user"
        value={userRole}
        disabled={!hasPermission}
        onValueChange={handleOnChange}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            value={TeamUserRolesEnum.ADMIN}
            disabled={!canAssignAdminRoles}
            className={!canAssignAdminRoles ? "hidden" : ""}
          >
            {t("components.dataTableUsers.roles.administrator")}
          </SelectItem>
          <SelectItem
            value={TeamUserRolesEnum.ADVISOR}
            disabled={!canAssignAdminRoles}
            className={!canAssignAdminRoles ? "hidden" : ""}
          >
            {t("components.dataTableUsers.roles.advisor")}
          </SelectItem>
          <SelectItem value={TeamUserRolesEnum.MANAGER}>
            {t("components.dataTableUsers.roles.manager")}
          </SelectItem>
          <SelectItem value={TeamUserRolesEnum.MEMBER}>
            {t("components.dataTableUsers.roles.member")}
          </SelectItem>
          <SelectItem value={TeamUserRolesEnum.BILLING}>
            {t("components.dataTableUsers.roles.billing")}
          </SelectItem>
        </SelectContent>
      </Select>

      <AlertDialog open={confirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("components.dataTableUsers.roles.confirmChangeTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("components.dataTableUsers.roles.confirmChangeDescription")}
              {isBillingRoleChange && (
                <>
                  <br />
                  <br />
                  <span className="font-medium text-warning">
                    ⚠️{" "}
                    {t(
                      "components.dataTableUsers.roles.confirmChangeToBillingWarning"
                    )}
                  </span>
                </>
              )}
              <br />
              <br />
              <span className="font-medium">
                {t("components.dataTableUsers.roles.before")}
              </span>{" "}
              {getTeamUserRoleLabel(userRole, t)}
              <br />
              <span className="font-medium">
                {t("components.dataTableUsers.roles.after")}
              </span>{" "}
              {pendingRole && getTeamUserRoleLabel(pendingRole, t)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={doCancel}>
              {t("components.dataTableUsers.roles.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={doConfirm}>
              {t("components.dataTableUsers.roles.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
